import { type CopySettings } from '@/features/copySettings';
import { copyCanonicalNote } from '@/helpers/copyHelpers/copyCanonical';
import {
  type COPY_SUPERSEDED,
  isFormattingOff,
  transformHtmlWithSettings,
  writeHtmlAndMarkdownToClipboard,
} from '@/helpers/copyHelpers/copyHelpers-v2';
import { reformatHtml } from '@/helpers/markdownToHtml';
import { type CopyFormattingLoss, renderHtmlToMarkdown } from '@heidi/editor/core';

/**
 * Which twin's note-level producer a selection copy should mirror. Declared by
 * the calling hook rather than sniffed from the editor: the twins serialize
 * through different pipelines, and inferring it would let a schema change
 * silently re-route the clipboard.
 */
type SelectionNoteSettingsCopier = (
  selectedHtml: string,
  settings: CopySettings
) => Promise<void>;

/**
 * vMarkdown route. Reuses the same preference — and the same transform engine
 * — as that editor's note-level Copy button, mirroring `copyWithSettingsV2`.
 * A whole-note selection and the note-level Copy button agree byte-for-byte.
 */
export const copyVMarkdownSelectionWithNoteSettings: SelectionNoteSettingsCopier = async (
  selectedHtml,
  settings
) => {
  const { html, markdown } = transformHtmlWithSettings({
    editorHtml: selectedHtml,
    settings,
  });

  if (isFormattingOff(settings)) {
    await writeHtmlAndMarkdownToClipboard({ markdown });
    return;
  }

  await writeHtmlAndMarkdownToClipboard({ html: reformatHtml(html), markdown });
};

/**
 * markdown-native route. Converts the selection HTML through the editor's
 * canonical renderer and applies the toggles with the same producer the
 * note-level Copy button uses for these notes, so the two agree by
 * construction. Never touches the legacy regex pipeline.
 *
 * Resolves the shared detector's formatting loss for this attempt (content-free
 * booleans, for `session_copied.formatting_removed`), or COPY_SUPERSEDED when a
 * newer copy replaced this one before it was published.
 */
export const copyMarkdownNativeSelectionWithNoteSettings = (
  selectedHtml: string,
  settings: CopySettings
): Promise<CopyFormattingLoss | typeof COPY_SUPERSEDED> =>
  copyCanonicalNote({
    markdownContent: renderHtmlToMarkdown(selectedHtml),
    settings,
    includeFormattingLoss: true,
  });
